import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Search, Mail, Phone, MapPin, BadgePercent, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";

const emptyForm = { first_name: "", last_name: "", email: "", phone: "", street: "", zip: "", city: "", discount_percent: 0, notes: "" };

function CustomerModal({ customer, onClose, onSaved }) {
  const [f, setF] = useState(customer ? { ...emptyForm, ...customer } : emptyForm);
  const [saving, setSaving] = useState(false);
  const s = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async () => {
    if (!f.first_name.trim() || !f.last_name.trim() || !f.email.trim()) {
      toast.error("Vorname, Nachname und E-Mail sind erforderlich.");
      return;
    }
    setSaving(true);
    const payload = {
      first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone,
      street: f.street, zip: f.zip, city: f.city,
      discount_percent: Number(f.discount_percent) || 0, notes: f.notes,
    };
    try {
      const { data } = customer
        ? await api.put(`/customers/${customer.id}`, payload)
        : await api.post("/customers", payload);
      onSaved(data, !customer);
      toast.success(customer ? "Kunde aktualisiert." : `Kunde angelegt – Kundennummer ${data.customer_number}.`);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} data-testid="customer-modal"
        className="relative glass rounded-3xl p-5 sm:p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl">{customer ? "Kunde bearbeiten" : "Neuer Kunde"}</h3>
          <button onClick={onClose} className="text-[#888] hover:text-white"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {customer?.customer_number && (
            <p className="font-mono text-xs text-[#888]">Kundennummer: <span className="text-white">{customer.customer_number}</span></p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="customer-first-name" value={f.first_name} onChange={s("first_name")} placeholder="Vorname" className={inputCls} />
            <input data-testid="customer-last-name" value={f.last_name} onChange={s("last_name")} placeholder="Nachname" className={inputCls} />
          </div>
          <input data-testid="customer-email" type="email" value={f.email} onChange={s("email")} placeholder="E-Mail" className={inputCls} />
          <input data-testid="customer-phone" value={f.phone} onChange={s("phone")} placeholder="Telefon" className={inputCls} />
          <input data-testid="customer-street" value={f.street} onChange={s("street")} placeholder="Straße & Nr." className={inputCls} />
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <input data-testid="customer-zip" value={f.zip} onChange={s("zip")} placeholder="PLZ" className={inputCls} />
            <input data-testid="customer-city" value={f.city} onChange={s("city")} placeholder="Ort" className={`${inputCls} col-span-2`} />
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Rabatt (%)</label>
            <input data-testid="customer-discount" type="number" min="0" max="100" step="0.5" value={f.discount_percent} onChange={s("discount_percent")} placeholder="z.B. 10" className={inputCls} />
            <p className="text-xs text-[#777] mt-2">Wird bei zukünftigen Bestellungen dieses Kunden automatisch vom Reparaturpreis abgezogen.</p>
          </div>
          <textarea data-testid="customer-notes" rows={3} value={f.notes} onChange={s("notes")} placeholder="Interne Notizen / bessere Konditionen (optional)" className={`${inputCls} resize-none`} />
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={save} disabled={saving} data-testid="customer-save" className="flex-1 rounded-full bg-white text-[#050505] py-3 font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
            {saving ? "Speichern…" : "Speichern"}
          </button>
          <button onClick={onClose} className="px-6 rounded-full border border-white/15 text-[#ccc] hover:text-white transition-colors">Abbrechen</button>
        </div>
      </motion.div>
    </div>
  );
}

export default function CustomersView() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState(null); // { customer: null | obj }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } catch (e) { toast.error("Kunden konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/customers/${id}`);
      setCustomers((p) => p.filter((c) => c.id !== id));
      toast.success("Kunde gelöscht.");
    } catch (e) { toast.error("Löschen fehlgeschlagen."); }
  };

  const copyNumber = (num) => {
    navigator.clipboard?.writeText(num);
    toast.success(`Kundennummer ${num} kopiert.`);
  };

  const q = query.trim().toLowerCase();
  const shown = customers.filter((c) => {
    if (!q) return true;
    const hay = `${c.first_name} ${c.last_name} ${c.email} ${c.customer_number}`.toLowerCase();
    return hay.includes(q);
  });

  return (
    <div data-testid="customers-view">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">Kunden</h1>
        <button onClick={() => setModal({ customer: null })} data-testid="customer-new-btn"
          className="inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-5 py-2.5 text-sm font-medium hover:bg-[#d1d5db] transition-colors">
          <Plus size={16} /> Neuer Kunde
        </button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#666]" />
          <input
            data-testid="customer-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Suche nach Name, E-Mail oder Kundennummer…"
            className="w-full bg-[#050505] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : shown.length === 0 ? (
        <div data-testid="customers-empty-state" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-16 text-center">
          <p className="text-[#888]">Noch keine Kunden angelegt.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="customers-list">
          {shown.map((c) => (
            <div key={c.id} data-testid={`customer-${c.id}`} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 items-start">
              <div className="lg:col-span-5">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => copyNumber(c.customer_number)} data-testid={`customer-number-${c.id}`}
                    className="font-mono text-xs text-white/90 bg-white/10 px-2 py-1 rounded-full inline-flex items-center gap-1.5 hover:bg-white/20 transition-colors">
                    {c.customer_number} <Copy size={11} />
                  </button>
                  {c.discount_percent > 0 && (
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
                      <BadgePercent size={12} /> {c.discount_percent}% Rabatt
                    </span>
                  )}
                </div>
                <p className="text-lg mt-2">{c.first_name} {c.last_name}</p>
                <div className="flex flex-col gap-1 mt-2 text-sm text-[#999]">
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Mail size={14} />{c.email}</a>
                  {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 hover:text-white transition-colors"><Phone size={14} />{c.phone}</a>}
                  {c.street && <span className="inline-flex items-start gap-2"><MapPin size={14} className="mt-0.5" />{c.street}, {c.zip} {c.city}</span>}
                </div>
              </div>
              <div className="lg:col-span-5">
                {c.notes && <p className="text-sm text-[#888] leading-relaxed">{c.notes}</p>}
              </div>
              <div className="lg:col-span-2 flex lg:justify-end gap-2">
                <button onClick={() => setModal({ customer: c })} data-testid={`customer-edit-${c.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-white hover:border-white/40 transition-colors"><Pencil size={16} /></button>
                <button onClick={() => remove(c.id)} data-testid={`customer-delete-${c.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {modal && (
          <CustomerModal
            customer={modal.customer}
            onClose={() => setModal(null)}
            onSaved={(d, isNew) => setCustomers((p) => (isNew ? [d, ...p] : p.map((c) => (c.id === d.id ? d : c))))}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
